import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type ReactNode,
} from "react";
import { loadFonts } from "../fonts";
import { MediaPicker } from "../MediaPicker";
import { Icon, IconName } from "../Icon";
import {
  TEXT_STYLES,
  newLayer,
  newPictureLayer,
  newSticker,
} from "../videoText";
import { ColorPicker, Modal, Slider, Tabs } from "../editor/Controls";
import { FontSelect } from "../editor/Panels";
import { CLIP_FILTERS, TRANSITIONS, clamp, fmt, outDur, r1 } from "./timeline";
import {
  deleteStudioMusic,
  uploadStudioAsset,
  uploadStudioMusic,
} from "../../../features/studio-controller/StudioServices";
import type { FabricObject } from "fabric";
import type { MediaItem } from "../../../types/studioTypes";
import { loadImage } from "../fabricKit";
import {
  MockupKind,
  makeChart,
  makeMockup,
  makeTable,
  renderToBlob,
  sampleChart,
  sampleTable,
} from "../elementKit";
import {
  AiElementForm,
  CategoryHead,
  CategoryTiles,
  ChartForm,
  ElementCategory,
  GraphicsGrid,
  MockupGrid,
  TableForm,
} from "../editor/ElementForms";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import {
  AudioItem,
  AudioRole,
  Clip,
  VideoPanelKey,
  VideoLayer as SharedVideoLayer,
  VideoDoc as SharedVideoDoc,
  LayerAnimIn,
  LayerAnimOut,
  LayerPatch,
  PictureLayer,
  TextLayer,
  StudioMusicTrack,
  Selection as SharedSelection,
} from "../../../types/videoType";

/* ================================================================
 * Types
 * ================================================================ */

type PanelName =
  | "media"
  | "clip"
  | "transition"
  | "text"
  | "elements"
  | "music"
  | "voice"
  | "size";

type Selection = SharedSelection;

/**
 * A layer as the panels read it: a text or picture layer whose fields of either kind can be read
 * (missing ones are undefined), so panels can show one form for stickers, text and pictures.
 */
type VideoLayer = SharedVideoLayer &
  Partial<Omit<TextLayer, "kind">> &
  Partial<Omit<PictureLayer, "kind">>;

/** A library track (music or an uploaded voiceover). */
type MusicTrack = StudioMusicTrack;

/** The editor's document, with layers in the panels' view. */
type VideoDoc = Omit<SharedVideoDoc, "layers"> & { layers: VideoLayer[] };

interface Brand {
  colors?: string[];
  logos?: Array<{
    galleryId: string | number;
    url: string;
  }>;
}

interface EditorFeatures {
  maxClips: number;
  maxVideoSeconds: number;
  maxHdVideoSeconds: number;
  maxAudios?: number;
  aiElements?: boolean;
}

/** The editor's recording (types/videoType Recording): live while speaking, done when it can be kept. */
type RecordingState =
  | { status: "live"; startT: number; since: number }
  | { status: "done"; startT: number; blob: Blob; url: string };

interface VideoEditor {
  panel: PanelName;

  doc: VideoDoc;
  sel?: Selection;

  features: EditorFeatures;
  brand: Brand;

  adding: boolean;

  total: number;
  t: number;
  playing: boolean;

  tracks: MusicTrack[] | null;
  recording?: RecordingState | null;

  setPanel: (panel: PanelName) => void;

  addClips: (items: MediaItem[], index?: number) => void | Promise<void>;

  updateClip: (id: string, patch: Partial<Clip>) => void;

  duplicateClip: (id: string) => void;
  moveClip: (from: number, to: number) => void;
  removeClip: (id: string) => void;

  split: () => void;

  addLayer: (layer: SharedVideoLayer) => void;

  updateLayer: (id: string, patch: LayerPatch) => void;

  removeLayer: (id: string) => void;

  setDoc: (patch: Partial<VideoDoc> | ((doc: VideoDoc) => VideoDoc)) => void;

  setTracks: (
    updater: MusicTrack[] | ((tracks: MusicTrack[]) => MusicTrack[]),
  ) => void;

  seek: (time: number) => void;

  startRecording: () => void;
  stopRecording: () => void;

  addAudios: (tracks: MusicTrack[], role: AudioRole) => void;
  updateAudio: (id: string, patch: Partial<AudioItem>, commit?: boolean) => void;
  removeAudio: (id: string) => void;
  uploadVoiceFiles: (files: File[]) => Promise<void>;
  voiceUploading?: boolean;
  setSel: (sel: Selection) => void;
}

interface TitleProps {
  children: ReactNode;
}

export const VIDEO_RAIL: Array<[VideoPanelKey, string, IconName]> = [
  ["media", "Media", "video"],
  ["clip", "Clip", "scissors"],
  ["transition", "Transitions", "sparkle"],
  ["text", "Text", "text"],
  ["elements", "Elements", "shapes"],
  ["music", "Music", "music"],
  ["voice", "Voiceover", "volume"],
  ["size", "Size", "resize"],
];

const ASPECTS = [
  {
    key: "PORTRAIT_9_16",
    label: "9:16",
    hint: "Status, Reels",
    ratio: 9 / 16,
  },
  {
    key: "SQUARE_1_1",
    label: "1:1",
    hint: "Chat, feed",
    ratio: 1,
  },
  {
    key: "LANDSCAPE_16_9",
    label: "16:9",
    hint: "YouTube",
    ratio: 16 / 9,
  },
  {
    key: "ORIGINAL",
    label: "Original",
    hint: "As recorded",
    ratio: null,
  },
] as const;

const STICKERS = [
  "🔥",
  "🎉",
  "⭐",
  "❤️",
  "✅",
  "🛍️",
  "🎁",
  "👉",
  "💯",
  "⏰",
  "📍",
  "🪔",
  "😍",
  "👍",
  "🙏",
  "✨",
  "🎊",
  "💥",
  "🚚",
  "🏷️",
  "📞",
  "💬",
  "🥳",
  "👇",
] as const;

const ANIM_IN = [
  ["NONE", "None"],
  ["FADE", "Fade"],
  ["SLIDE_UP", "Rise"],
  ["SLIDE_DOWN", "Drop"],
  ["SLIDE_LEFT", "From right"],
  ["SLIDE_RIGHT", "From left"],
  ["POP", "Pop"],
] as const;

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/* ================================================================
 * Shared Tailwind class fragments
 * ================================================================ */

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

const PANEL_BODY = "px-[18px] pt-[18px] pb-7";
const PANEL_H3 =
  "mt-[22px] mb-2.5 text-[13px] font-bold text-st-muted uppercase tracking-[0.06em]";
const HINT = "mt-2 text-[13px] leading-[1.5] text-st-muted";
const HINT_TOP = "mt-0 mb-3";
const FINEPRINT = "mt-2 text-xs text-st-muted leading-[1.45]";
const FIELD_LABEL = "block text-[13px] font-semibold mt-3.5 mb-1.5";
const INPUT =
  "w-full text-sm px-[11px] py-[9px] rounded-[9px] border border-st-line bg-white text-st-ink resize-y";
const SELECT =
  "w-auto min-h-[36px] pl-2.5 pr-[30px] py-1.5 rounded-[9px] border border-st-line bg-white text-st-ink cursor-pointer";
const CHECK =
  "flex items-center gap-2 text-sm cursor-pointer my-2.5 [&>input]:w-[17px] [&>input]:h-[17px] [&>input]:accent-st-green [&>input]:flex-none";

const LINK =
  "bg-transparent border-0 py-1 text-st-green-dark font-semibold text-sm cursor-pointer inline-flex items-center gap-1.5";
const LINK_DANGER = "text-st-red mt-2.5";
const BTN =
  "inline-flex items-center justify-center gap-2 min-h-10 px-3.5 py-[9px] rounded-[10px] border border-st-line bg-white text-st-ink font-semibold text-sm leading-none whitespace-nowrap cursor-pointer hover:border-[#9fb3ab] disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SMALL = "min-h-[34px] px-[11px] py-[7px] text-[13px] rounded-[9px]";
const BTN_BLOCK = "w-full";

const SEG =
  "inline-flex p-[3px] gap-0.5 rounded-[10px] bg-st-paper border border-st-line [&>button]:min-w-8 [&>button]:h-[30px] [&>button]:px-2 [&>button]:border-0 [&>button]:rounded-[7px] [&>button]:bg-transparent [&>button]:text-st-muted [&>button]:text-sm [&>button]:cursor-pointer [&>button]:inline-grid [&>button]:place-items-center [&>button[aria-pressed='true']]:bg-white [&>button[aria-pressed='true']]:text-st-ink [&>button[aria-pressed='true']]:shadow-[0_1px_3px_rgba(16,35,30,0.15)]";
const SEG_WIDE_OR_FULL =
  "flex flex-wrap w-full [&>button]:flex-1 [&>button]:text-[13px] [&>button]:font-semibold";

/* ================================================================
 * Main panels
 * ================================================================ */

interface VideoPanelsProps {
  ed: VideoEditor;
}

export function VideoPanels({ ed }: VideoPanelsProps) {
  switch (ed.panel) {
    case "media":
      return <MediaPanel ed={ed} />;

    case "clip":
      return <ClipPanel ed={ed} />;

    case "transition":
      return <TransitionPanel ed={ed} />;

    case "text":
      return <TextPanel ed={ed} />;

    case "elements":
      return <ElementsPanel ed={ed} />;

    case "music":
      return <MusicPanel ed={ed} />;

    case "voice":
      return <VoicePanel ed={ed} />;

    case "size":
      return <SizePanel ed={ed} />;

    default:
      return null;
  }
}

function Title({ children }: TitleProps) {
  return (
    <h2 className="mb-3.5 font-['Bricolage_Grotesque','IBM_Plex_Sans',sans-serif] text-[19px]">
      {children}
    </h2>
  );
}

/* ================================================================
 * Media
 * ================================================================ */

interface PanelProps {
  ed: VideoEditor;
}

function MediaPanel({ ed }: PanelProps) {
  const [tab, setTab] = useState<"VIDEO" | "IMAGE">("VIDEO");

  const k =
    ed.sel?.type === "clip"
      ? ed.doc.clips.findIndex((c) => c.id === ed.sel?.id)
      : -1;

  return (
    <div className={PANEL_BODY}>
      <Title>Media</Title>

      <Tabs
        value={tab}
        onChange={(value) => setTab(value as "VIDEO" | "IMAGE")}
        items={[
          ["VIDEO", "Videos"],
          ["IMAGE", "Photos"],
        ]}
      />

      <p className={cx(HINT, HINT_TOP)}>
        Click to add {k >= 0 ? "after the selected clip" : "at the end"}.{" "}
        {ed.doc.clips.length} of {ed.features.maxClips} clips.
      </p>

      {ed.adding && (
        <p className="mb-3 px-3 py-2.5 rounded-[10px] bg-st-green-soft text-st-green-dark text-[13px] font-semibold">
          Adding…
        </p>
      )}

      <MediaPicker
        key={tab}
        type={tab}
        busy={ed.adding}
        onPick={(item) => ed.addClips([item], k >= 0 ? k + 1 : undefined)}
      />
    </div>
  );
}

/* ================================================================
 * Clip
 * ================================================================ */

function ClipPanel({ ed }: PanelProps) {
  const { doc } = ed;

  const k =
    ed.sel?.type === "clip"
      ? doc.clips.findIndex((c) => c.id === ed.sel?.id)
      : -1;

  const c = doc.clips[k];

  if (!c) {
    return (
      <div className={PANEL_BODY}>
        <Title>Clip</Title>

        <p className={cx(HINT, HINT_TOP)}>
          Click a clip in the timeline to trim it, change its speed, filter or
          sound, or split it.
        </p>
      </div>
    );
  }

  const set = (patch: Partial<Clip>) => ed.updateClip(c.id, patch);

  return (
    <div className={PANEL_BODY}>
      <Title>{c.image ? "Photo clip" : "Video clip"}</Title>

      <p className={cx(HINT, HINT_TOP)}>
        <strong>{c.name}</strong> · clip {k + 1} of {doc.clips.length} ·{" "}
        {fmt(outDur(c))} on screen
      </p>

      {c.image ? (
        <>
          <Slider
            label="Show for"
            value={c.duration}
            min={1}
            max={15}
            step={0.5}
            suffix=" s"
            onChange={(v) => set({ duration: v })}
          />

          <label className={CHECK}>
            <input
              type="checkbox"
              checked={c.kenBurns}
              onChange={(e) => set({ kenBurns: e.target.checked })}
            />
            Slow zoom (Ken Burns)
          </label>
        </>
      ) : (
        <>
          <h3 className={PANEL_H3}>Trim</h3>

          <div className="grid md:grid-cols-2 gap-2.5 mt-3 [&_label]:grid [&_label]:gap-1 [&_label]:text-xs [&_label]:font-semibold [&_label]:text-st-muted [&_input]:w-full [&_input]:text-sm [&_input]:px-[9px] [&_input]:py-[7px] [&_input]:border [&_input]:border-st-line [&_input]:rounded-lg [&_input]:tabular-nums">
            <label>
              Start (s)
              <input
                type="number"
                step="0.1"
                min={0}
                value={r1(c.trimStart)}
                onChange={(e) =>
                  set({
                    trimStart: clamp(
                      Number(e.target.value),
                      0,
                      c.trimEnd - 0.5,
                    ),
                  })
                }
              />
            </label>

            <label>
              End (s)
              <input
                type="number"
                step="0.1"
                value={r1(c.trimEnd)}
                onChange={(e) =>
                  set({
                    trimEnd: clamp(
                      Number(e.target.value),
                      c.trimStart + 0.5,
                      c.sourceDuration || 9999,
                    ),
                  })
                }
              />
            </label>
          </div>

          <p className={FINEPRINT}>
            The video is {fmt(c.sourceDuration ?? 0)} long. You can also drag
            the ends of the clip in the timeline.
          </p>

          <h3 className={PANEL_H3}>Speed</h3>

          <div className={cx(SEG, SEG_WIDE_OR_FULL)}>
            {SPEEDS.map((s) => (
              <button
                key={s}
                aria-pressed={c.speed === s}
                onClick={() => set({ speed: s })}
              >
                {s}×
              </button>
            ))}
          </div>

          <h3 className={PANEL_H3}>Sound</h3>

          <label className={CHECK}>
            <input
              type="checkbox"
              checked={c.muted}
              onChange={(e) => set({ muted: e.target.checked })}
            />
            Mute this clip
          </label>

          {!c.muted && (
            <Slider
              label="Volume"
              value={Math.round(c.volume * 100)}
              min={0}
              max={200}
              suffix="%"
              onChange={(v) => set({ volume: v / 100 })}
            />
          )}
        </>
      )}

      <h3 className={PANEL_H3}>Filter</h3>

      <div className="grid grid-cols-4 gap-2">
        {CLIP_FILTERS.map(([key, label, css]) => {
          const on = c.filter === key;
          return (
            <button
              key={key}
              className={cx(
                "grid gap-1 p-0 border-0 bg-transparent cursor-pointer text-[11.5px] text-st-ink",
                on && "[&>small]:font-bold [&>small]:text-st-green-dark",
              )}
              onClick={() => set({ filter: key })}
            >
              <span
                className={cx(
                  "block aspect-square rounded-[9px] overflow-hidden bg-[#1a2a25] [&>img]:w-full [&>img]:h-full [&>img]:object-cover [&>img]:block [&>video]:w-full [&>video]:h-full [&>video]:object-cover [&>video]:block",
                  on && "shadow-[0_0_0_3px_theme(colors.st.green)]",
                )}
                style={{ filter: css }}
              >
                {c.image ? (
                  <img src={c.url} alt="" />
                ) : (
                  <video
                    src={`${c.url}#t=${(c.trimStart + 0.3).toFixed(1)}`}
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
              </span>

              <small>{label}</small>
            </button>
          );
        })}
      </div>

      <button
        className={LINK}
        onClick={() =>
          doc.clips.forEach((x) =>
            ed.updateClip(x.id, {
              filter: c.filter,
            }),
          )
        }
      >
        Use this filter on every clip
      </button>

      <h3 className={PANEL_H3}>Clip</h3>

      <div className="grid grid-cols-2 gap-1.5">
        <button className={cx(BTN, BTN_SMALL)} onClick={ed.split}>
          <Icon name="scissors" size={16} />
          Split at playhead
        </button>

        <button
          className={cx(BTN, BTN_SMALL)}
          onClick={() => ed.duplicateClip(c.id)}
        >
          <Icon name="copy" size={16} />
          Duplicate
        </button>

        <button
          className={cx(BTN, BTN_SMALL)}
          disabled={k === 0}
          onClick={() => ed.moveClip(k, k - 1)}
        >
          <Icon name="back" size={16} />
          Move earlier
        </button>

        <button
          className={cx(BTN, BTN_SMALL)}
          disabled={k === doc.clips.length - 1}
          onClick={() => ed.moveClip(k, k + 1)}
        >
          Move later
          <Icon
            name="back"
            size={16}
            style={{
              transform: "scaleX(-1)",
            }}
          />
        </button>
      </div>

      <button
        className={cx(LINK, LINK_DANGER)}
        disabled={doc.clips.length === 1}
        onClick={() => ed.removeClip(c.id)}
      >
        Delete clip
      </button>
    </div>
  );
}

/* ================================================================
 * Transitions
 * ================================================================ */

/** Tailwind-only approximation of the old per-transition CSS keyframe previews. */
function transDemoOverlayClasses(key: string, active: boolean) {
  const base =
    "absolute inset-0 bg-gradient-to-br from-[#f97316] to-[#ffd166] transition-all duration-700 ease-in-out";
  switch (key) {
    case "SLIDELEFT":
    case "SMOOTHLEFT":
      return cx(
        base,
        active ? "translate-x-0" : "translate-x-full group-hover:translate-x-0",
      );
    case "SLIDERIGHT":
      return cx(
        base,
        active
          ? "translate-x-0"
          : "-translate-x-full group-hover:translate-x-0",
      );
    case "SLIDEUP":
      return cx(
        base,
        active ? "translate-y-0" : "translate-y-full group-hover:translate-y-0",
      );
    case "WIPELEFT":
      return cx(
        base,
        active
          ? "[clip-path:inset(0)]"
          : "[clip-path:inset(0_0_0_100%)] group-hover:[clip-path:inset(0)]",
      );
    case "WIPERIGHT":
      return cx(
        base,
        active
          ? "[clip-path:inset(0)]"
          : "[clip-path:inset(0_100%_0_0)] group-hover:[clip-path:inset(0)]",
      );
    case "CIRCLEOPEN":
    case "RADIAL":
      return cx(
        base,
        active
          ? "[clip-path:circle(75%_at_50%_50%)]"
          : "[clip-path:circle(0%_at_50%_50%)] group-hover:[clip-path:circle(75%_at_50%_50%)]",
      );
    case "PIXELIZE":
      return cx(
        base,
        active
          ? "opacity-100 scale-100"
          : "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100",
      );
    case "NONE":
      return cx(
        base,
        active
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100 group-hover:delay-300",
      );
    default:
      // FADE, DISSOLVE, FADEBLACK, FADEWHITE
      return cx(
        base,
        active ? "opacity-100" : "opacity-0 group-hover:opacity-100",
      );
  }
}

function TransitionPanel({ ed }: PanelProps) {
  const { doc } = ed;

  let k =
    ed.sel?.type === "transition"
      ? doc.clips.findIndex((c) => c.id === ed.sel?.id)
      : -1;

  if (k < 0 && ed.sel?.type === "clip") {
    k = doc.clips.findIndex((c) => c.id === ed.sel?.id);
  }

  if (k >= doc.clips.length - 1) {
    k = doc.clips.length - 2;
  }

  const c = doc.clips[k];

  if (!c) {
    return (
      <div className={PANEL_BODY}>
        <Title>Transitions</Title>

        <p className={cx(HINT, HINT_TOP)}>
          Add a second clip, then pick how one clip changes into the next.
        </p>

        <button
          className={cx(BTN, BTN_SMALL)}
          onClick={() => ed.setPanel("media")}
        >
          <Icon name="plus" size={16} />
          Add a clip
        </button>
      </div>
    );
  }

  const set = (patch: Partial<Clip>) => ed.updateClip(c.id, patch);

  return (
    <div className={PANEL_BODY}>
      <Title>Transitions</Title>

      <p className={cx(HINT, HINT_TOP)}>
        Between clip {k + 1} and clip {k + 2}. Click the <strong>+</strong>{" "}
        between clips in the timeline to pick another join.
      </p>

      <div className="grid grid-cols-3 gap-2">
        {TRANSITIONS.map(([key, label]) => {
          const on = c.transition === key;
          return (
            <button
              key={key}
              className={cx(
                "group grid gap-1 justify-items-center px-1 pt-1.5 pb-2 border rounded-[10px] bg-white cursor-pointer text-xs text-st-ink",
                on
                  ? "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_theme(colors.st.green)]"
                  : "border-st-line",
              )}
              onClick={() => set({ transition: key })}
            >
              <span className="relative w-full aspect-[16/10] rounded-md overflow-hidden bg-st-green">
                <i className="absolute inset-0 bg-gradient-to-br from-st-green to-[#25d366]" />
                <b className={transDemoOverlayClasses(key, on)} />
              </span>

              <small>{label}</small>
            </button>
          );
        })}
      </div>

      {c.transition !== "NONE" && (
        <Slider
          label="Length"
          value={c.transitionDuration}
          min={0.2}
          max={1.5}
          step={0.1}
          suffix=" s"
          onChange={(v) => set({ transitionDuration: v })}
        />
      )}

      <button
        className={cx(BTN, BTN_SMALL, BTN_BLOCK, "mt-3")}
        onClick={() =>
          doc.clips.slice(0, -1).forEach((x) =>
            ed.updateClip(x.id, {
              transition: c.transition,
              transitionDuration: c.transitionDuration,
            }),
          )
        }
      >
        Use “{TRANSITIONS?.find((x) => x[0] === c.transition)?.[1]}” between all
        clips
      </button>

      <p className={FINEPRINT}>
        Transitions overlap the two clips, so the video gets a little shorter.
      </p>
    </div>
  );
}

/* ================================================================
 * Text
 * ================================================================ */

function TextPanel({ ed }: PanelProps) {
  const { doc } = ed;

  const layer =
    ed.sel?.type === "layer"
      ? doc.layers.find(
          (l) => l.id === ed.sel?.id && !l.sticker && l.kind !== "image",
        )
      : null;

  useEffect(() => {
    void loadFonts(TEXT_STYLES.map((s) => s.font));
  }, []);

  return (
    <div className={PANEL_BODY}>
      <Title>Text</Title>

      <div className="grid grid-cols-2 gap-2">
        {TEXT_STYLES.map((s) => (
          <button
            key={s.key}
            className="min-h-16 grid place-items-center rounded-xl border border-st-line bg-[#3a4a45] bg-gradient-to-br from-[#3d5a50] to-[#23332e] cursor-pointer text-lg hover:shadow-[0_0_0_2px_theme(colors.web.primary)]"
            onClick={() =>
              ed.addLayer(
                newLayer(
                  s.key,
                  ed.total,
                  s.key === "hindi" ? "नमस्ते" : s.label,
                ),
              )
            }
            style={{
              fontFamily: `"${s.font}"`,
              fontWeight: s.weight,
              fontStyle: s.italic ? "italic" : "normal",
            }}
          >
            <span
              className={cx(
                "px-2 py-0.5 rounded-md",
                s.mode === "shadow" &&
                  "[text-shadow:0_2px_6px_rgba(0,0,0,0.5)]",
              )}
              style={{
                color: s.color,
                background: s.mode === "box" ? s.bg : undefined,
                WebkitTextStroke:
                  s.mode === "outline" ? `1px ${s.stroke}` : undefined,
                textShadow: s.mode === "neon" ? `0 0 8px ${s.glow}` : undefined,
              }}
            >
              {s.label}
            </span>
          </button>
        ))}
      </div>

      {layer && <TextEditor ed={ed} layer={layer} />}

      {!layer && doc.layers.some((l) => !l.sticker && l.kind !== "image") && (
        <p className={HINT}>
          Click text on the video or in the timeline to edit it.
        </p>
      )}
    </div>
  );
}

interface TextEditorProps {
  ed: VideoEditor;
  layer: VideoLayer;
}

function TextEditor({ ed, layer }: TextEditorProps) {
  const set = (patch: LayerPatch) => ed.updateLayer(layer.id, patch);

  const brandColors = ed.brand.colors || [];

  return (
    <div className="mt-[18px] pt-1 border-t border-st-line">
      <label className={FIELD_LABEL} htmlFor="st-vlayer-text">
        Text
      </label>

      <textarea
        id="st-vlayer-text"
        className={INPUT}
        rows={3}
        value={layer.text ?? ""}
        maxLength={300}
        onChange={(e) => set({ text: e.target.value })}
      />

      <div className="flex flex-wrap gap-2 items-center mt-2.5">
        <FontSelect
          value={layer.font ?? ""}
          style={{
            fontFamily: `"${layer.font ?? ""}"`,
            flex: 1,
          }}
          onChange={async (font: string) => {
            await loadFonts([font]);
            set({ font });
          }}
        />

        <div className={SEG}>
          {(
            [
              ["left", "alignLeft"],
              ["center", "alignCenter"],
              ["right", "alignRight"],
            ] as const
          ).map(([align, icon]) => (
            <button
              key={align}
              aria-pressed={layer.align === align}
              onClick={() => set({ align })}
              title={`Align ${align}`}
            >
              <Icon name={icon} size={16} />
            </button>
          ))}
        </div>
      </div>

      <Slider
        label="Size"
        value={Math.round((layer.size ?? 0) * 1000) / 10}
        min={2}
        max={15}
        step={0.5}
        suffix="%"
        onChange={(v) => set({ size: v / 100 })}
      />

      <div className={FIELD_LABEL}>Style</div>

      <div className={cx(SEG, SEG_WIDE_OR_FULL)}>
        {(
          [
            ["shadow", "Shadow"],
            ["box", "Box"],
            ["outline", "Outline"],
            ["neon", "Glow"],
            ["plain", "Plain"],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={mode}
            aria-pressed={layer.mode === mode}
            onClick={() => set({ mode })}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2.5 text-sm my-2.5">
        <span>Text colour</span>

        <ColorPicker
          value={layer.color ?? "#ffffff"}
          onChange={(value) =>
            set({
              color: value || "#ffffff",
            })
          }
          label="Text colour"
          variant="letter"
          brandColors={brandColors}
        />
      </div>

      {layer.mode === "box" && (
        <div className="flex items-center justify-between gap-2.5 text-sm my-2.5">
          <span>Box colour</span>

          <ColorPicker
            value={layer.bg ?? "#ffffff"}
            onChange={(value) =>
              set({
                bg: value || "#ffffff",
              })
            }
            label="Box colour"
            brandColors={brandColors}
          />
        </div>
      )}

      {layer.mode === "outline" && (
        <div className="flex items-center justify-between gap-2.5 text-sm my-2.5">
          <span>Outline colour</span>

          <ColorPicker
            value={layer.stroke ?? "#000000"}
            onChange={(value) =>
              set({
                stroke: value || "#000000",
              })
            }
            label="Outline colour"
            brandColors={brandColors}
          />
        </div>
      )}

      {layer.mode === "neon" && (
        <div className="flex items-center justify-between gap-2.5 text-sm my-2.5">
          <span>Glow colour</span>

          <ColorPicker
            value={layer.glow ?? "#25d366"}
            onChange={(value) =>
              set({
                glow: value || "#25d366",
              })
            }
            label="Glow colour"
            brandColors={brandColors}
          />
        </div>
      )}

      <AnimTiming ed={ed} layer={layer} />

      <button
        className={cx(LINK, LINK_DANGER)}
        onClick={() => ed.removeLayer(layer.id)}
      >
        Remove text
      </button>
    </div>
  );
}

/* ================================================================
 * Animation timing
 * ================================================================ */

interface AnimTimingProps {
  ed: VideoEditor;
  layer: VideoLayer;
}

function AnimTiming({ ed, layer }: AnimTimingProps) {
  const set = (patch: LayerPatch) => ed.updateLayer(layer.id, patch);

  return (
    <>
      <div className={FIELD_LABEL}>Animation</div>

      <div className="flex flex-wrap gap-2 items-center mt-2.5">
        <label className="grid gap-[3px] text-xs font-semibold text-st-muted">
          In
          <select
            className={SELECT}
            value={layer.animIn || "NONE"}
            onChange={(e) =>
              set({
                animIn: e.target.value as LayerAnimIn, // options come from ANIM_IN
              })
            }
          >
            {ANIM_IN.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-[3px] text-xs font-semibold text-st-muted">
          Out
          <select
            className={SELECT}
            value={layer.animOut || "NONE"}
            onChange={(e) =>
              set({
                animOut: e.target.value as LayerAnimOut,
              })
            }
          >
            <option value="NONE">None</option>

            <option value="FADE">Fade</option>
          </select>
        </label>

        <button
          className={cx(BTN, BTN_SMALL)}
          onClick={() => {
            ed.seek(layer.start);
          }}
          title="Jump to where it appears"
        >
          <Icon name="play" size={14} />
          Preview
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mt-3 [&_label]:grid [&_label]:gap-1 [&_label]:text-xs [&_label]:font-semibold [&_label]:text-st-muted [&_input]:w-full [&_input]:text-sm [&_input]:px-[9px] [&_input]:py-[7px] [&_input]:border [&_input]:border-st-line [&_input]:rounded-lg [&_input]:tabular-nums">
        <label>
          Show from (s)
          <input
            type="number"
            step="0.1"
            min={0}
            value={r1(layer.start)}
            onChange={(e) =>
              set({
                start: clamp(Number(e.target.value), 0, layer.end - 0.5),
              })
            }
          />
        </label>

        <label>
          Until (s)
          <input
            type="number"
            step="0.1"
            value={r1(layer.end)}
            onChange={(e) =>
              set({
                end: clamp(Number(e.target.value), layer.start + 0.5, ed.total),
              })
            }
          />
        </label>
      </div>

      <button
        className={LINK}
        onClick={() =>
          set({
            start: 0,
            end: r1(ed.total),
          })
        }
      >
        Show for the whole video
      </button>
    </>
  );
}

/* ================================================================
 * Elements
 * ================================================================ */

function ElementsPanel({ ed }: PanelProps) {
  const { doc } = ed;
  const dispatch = useDispatch<AppDispatch>();

  const layer =
    ed.sel?.type === "layer"
      ? doc.layers.find(
          (l) => l.id === ed.sel?.id && (l.sticker || l.kind === "image"),
        )
      : null;

  const [cat, setCat] = useState<ElementCategory | null>(null);
  const [picking, setPicking] = useState<boolean>(false);
  const [mockup, setMockup] = useState<MockupKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brandColor = ed.brand.colors?.[0] || "#0b7a5a";

  const set = (patch: LayerPatch) => {
    if (layer) {
      ed.updateLayer(layer.id, patch);
    }
  };

  /** A Gallery picture as a layer in the middle of the video, `w` of its width. */
  function addPictureItem(item: { mediaUrl: string; templateName?: string | null }, w = 0.4) {
    ed.addLayer({ ...newPictureLayer(item, ed.total), x: 0.5, y: 0.5, w });
  }

  /** Draws elements to a PNG, keeps it in the Gallery, and adds it as a picture layer. */
  async function addDrawn(objects: FabricObject[], name: string, w: number) {
    setBusy(true);
    setError(null);
    try {
      const blob = await renderToBlob(objects, 1000);
      const file = new File([blob], `${name.toLowerCase().replace(/[^a-z0-9]+/g, "_")}.png`, { type: "image/png" });
      const item = (await dispatch(uploadStudioAsset(file)).unwrap()) as MediaItem;
      addPictureItem({ mediaUrl: item.mediaUrl, templateName: name }, w);
    } catch (e) {
      setError(e instanceof Error ? e.message : typeof e === "string" ? e : "Couldn't add that element.");
    } finally {
      setBusy(false);
    }
  }

  async function addMockup(kind: MockupKind, item: MediaItem) {
    setBusy(true);
    setError(null);
    try {
      const img = await loadImage(item.mediaUrl);
      setBusy(false);
      await addDrawn(makeMockup(kind, 1000, 1000, img, 1), "Mockup", 0.5);
      setMockup(null);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't load that photo.");
    }
  }

  const k = ed.sel?.type === "clip" ? doc.clips.findIndex((c) => c.id === ed.sel?.id) : -1;

  return (
    <div className={PANEL_BODY}>
      <Title>Elements</Title>

      {busy && (
        <p className="mb-3 px-3 py-2.5 rounded-[10px] bg-st-green-soft text-st-green-dark text-[13px] font-semibold">
          Adding…
        </p>
      )}
      {error && (
        <p className="text-st-red text-[13px] mb-3" role="alert">
          {error}
        </p>
      )}

      {!cat && <CategoryTiles onPick={(c) => { setCat(c); setError(null); }} />}

      {cat && <CategoryHead cat={cat} onBack={() => { setCat(null); setMockup(null); }} />}

      {(!cat || cat === "graphics") && (
        <>
          <h3 className={PANEL_H3}>Stickers</h3>

          <div className="grid grid-cols-8 gap-1.5">
            {STICKERS.map((emoji) => (
              <button
                key={emoji}
                className="aspect-square text-[20px] border-0 rounded-[10px] bg-st-paper cursor-pointer hover:bg-st-green-soft hover:scale-[1.06]"
                onClick={() => ed.addLayer(newSticker(emoji, ed.total))}
                aria-label={`Add ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}

      {cat === "graphics" && (
        <>
          <h3 className={PANEL_H3}>Graphics</h3>
          <GraphicsGrid color={brandColor} busy={busy} onPick={(g) => addDrawn([g.make(brandColor)], g.label, 0.3)} />
        </>
      )}

      {(!cat || cat === "photos") && (
        <>
          {ed.brand.logos && ed.brand.logos.length > 0 && (
            <>
              <h3 className={PANEL_H3}>Your logos</h3>

              <div className="grid grid-cols-3 gap-2 mt-3.5">
                {ed.brand.logos.map((logo) => (
                  <button
                    key={logo.galleryId}
                    className="relative aspect-square p-0 border-0 rounded-[10px] overflow-hidden cursor-pointer [background:repeating-conic-gradient(#eef2f0_0_25%,#fff_0_50%)_0_0/14px_14px] [&>img]:w-full [&>img]:h-full [&>img]:object-contain [&>img]:p-1.5"
                    onClick={() =>
                      ed.addLayer(
                        newPictureLayer(
                          {
                            mediaUrl: logo.url,
                            templateName: "Logo",
                          },
                          ed.total,
                        ),
                      )
                    }
                  >
                    <img src={logo.url} alt="Logo" />
                  </button>
                ))}
              </div>
            </>
          )}

          <h3 className={PANEL_H3}>Pictures</h3>

          {picking || cat === "photos" ? (
            <>
              <MediaPicker
                type="IMAGE"
                compact
                onPick={(item) => {
                  setPicking(false);

                  ed.addLayer(newPictureLayer(item, ed.total));
                }}
              />

              {!cat && (
                <button className={LINK} onClick={() => setPicking(false)}>
                  Cancel
                </button>
              )}
            </>
          ) : (
            <button
              className={cx(BTN, BTN_SMALL, BTN_BLOCK)}
              onClick={() => setPicking(true)}
            >
              <Icon name="image" size={16} />
              Add a logo or photo on top
            </button>
          )}
        </>
      )}

      {cat === "videos" && (
        <>
          <p className={cx(HINT, HINT_TOP)}>
            Videos are added as clips {k >= 0 ? "after the selected clip" : "at the end"}.
          </p>
          <MediaPicker
            type="VIDEO"
            compact
            busy={ed.adding}
            onPick={(item) => ed.addClips([item], k >= 0 ? k + 1 : undefined)}
          />
        </>
      )}

      {cat === "audio" && (
        <>
          <p className={cx(HINT, HINT_TOP)}>
            Click a track to add it to the timeline. Fades, trimming and splitting are in{" "}
            <button className={LINK} onClick={() => ed.setPanel("music")}>Music</button>.
          </p>
          <MusicLibrary
            tracks={ed.tracks}
            usedIds={doc.audios.map((a) => a.trackId)}
            onSelect={(track) => ed.addAudios([track], "music")}
            onUploaded={(tracks) => {
              ed.setTracks((list) => [...tracks, ...(list || [])]);
              ed.addAudios(tracks, "music");
            }}
            onDeleted={(trackId) => {
              ed.setTracks((list) => (list || []).filter((t) => t.id !== trackId));
              doc.audios.filter((a) => a.trackId === trackId).forEach((a) => ed.removeAudio(a.id));
            }}
          />
        </>
      )}

      {cat === "ai" && (
        <AiElementForm enabled={!!ed.features.aiElements} onMade={(item) => addPictureItem(item, 0.35)} />
      )}

      {cat === "mockups" &&
        (mockup ? (
          <>
            <p className={cx(HINT, HINT_TOP)}>Pick the photo to show on the screen.</p>
            <MediaPicker type="IMAGE" compact busy={busy} onPick={(item) => addMockup(mockup, item)} />
            <button className={LINK} onClick={() => setMockup(null)}>
              Choose another device
            </button>
          </>
        ) : (
          <MockupGrid busy={busy} onPick={setMockup} />
        ))}

      {cat === "charts" && (
        <ChartForm
          initial={sampleChart("column", ed.brand.colors?.length ? ed.brand.colors : undefined)}
          brandColors={ed.brand.colors}
          submitLabel="Add chart to the video"
          busy={busy}
          onSubmit={(spec) => addDrawn([makeChart(spec)], "Chart", 0.8)}
        />
      )}

      {cat === "tables" && (
        <TableForm
          initial={sampleTable(brandColor)}
          brandColors={ed.brand.colors}
          submitLabel="Add table to the video"
          busy={busy}
          onSubmit={(spec) => addDrawn([makeTable(spec)], "Table", 0.85)}
        />
      )}

      {layer && (
        <div className="mt-[18px] pt-1 border-t border-st-line">
          <p className={cx(HINT, HINT_TOP)}>
            Drag it on the video to place it.
          </p>

          {layer.kind === "image" ? (
            <>
              <Slider
                label="Size"
                value={Math.round((layer.w ?? 0) * 100)}
                min={5}
                max={100}
                suffix="%"
                onChange={(v) =>
                  set({
                    w: v / 100,
                  })
                }
              />

              <Slider
                label="Rounded corners"
                value={layer.radius || 0}
                min={0}
                max={100}
                suffix="%"
                onChange={(v) =>
                  set({
                    radius: v,
                  })
                }
              />
            </>
          ) : (
            <Slider
              label="Size"
              value={Math.round((layer.size ?? 0) * 100)}
              min={4}
              max={40}
              suffix="%"
              onChange={(v) =>
                set({
                  size: v / 100,
                })
              }
            />
          )}

          <AnimTiming ed={ed} layer={layer} />

          <button
            className={cx(LINK, LINK_DANGER)}
            onClick={() => ed.removeLayer(layer.id)}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

/* ================================================================
 * Music
 * ================================================================ */

function MusicPanel({ ed }: PanelProps) {
  const { doc } = ed;
  const pieces = doc.audios.filter((a) => a.role === "music");
  const selected =
    ed.sel?.type === "audio"
      ? pieces.find((a) => a.id === ed.sel?.id) ?? null
      : null;

  function add(tracks: MusicTrack[]) {
    const first = !doc.audios.some((a) => a.role === "music");
    ed.addAudios(tracks, "music");
    // background music: lower the clips' own sound so the music comes through
    if (first) {
      doc.clips.forEach((clip) => {
        if (!clip.image && clip.volume === 1 && !clip.muted) {
          ed.updateClip(clip.id, { volume: 0.35 });
        }
      });
    }
  }

  return (
    <div className={PANEL_BODY}>
      <Title>Music</Title>

      <p className={cx(HINT, HINT_TOP)}>
        Click a track to add it. The first one plays for the whole video; more
        tracks are added one after another from the playhead. Select a track in
        the timeline to move, trim, fade or split it.
      </p>

      <AudioPieces ed={ed} pieces={pieces} />

      {selected && <AudioSettings ed={ed} piece={selected} />}

      <h3 className={PANEL_H3}>Library</h3>

      <MusicLibrary
        tracks={ed.tracks}
        usedIds={pieces.map((a) => a.trackId)}
        onSelect={(track) => add([track])}
        onUploaded={(tracks) => {
          ed.setTracks((list) => [...tracks, ...(list || [])]);
          add(tracks);
        }}
        onDeleted={(trackId) => {
          ed.setTracks((list) => (list || []).filter((t) => t.id !== trackId));
          doc.audios
            .filter((a) => a.trackId === trackId)
            .forEach((a) => ed.removeAudio(a.id));
        }}
      />
    </div>
  );
}

/** The pieces of one kind on the timeline, as a list to pick from. */
function AudioPieces({ ed, pieces }: { ed: VideoEditor; pieces: AudioItem[] }) {
  if (!pieces.length) return null;
  return (
    <ul className="list-none m-0 mb-1 p-0 grid gap-1">
      {pieces.map((a) => {
        const on = ed.sel?.type === "audio" && ed.sel.id === a.id;
        return (
          <li key={a.id}>
            <button
              type="button"
              className={cx(
                "w-full flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left cursor-pointer",
                on
                  ? "border-st-green bg-st-green-soft"
                  : "border-st-line bg-white hover:bg-st-paper",
              )}
              onClick={() => {
                ed.setSel({ type: "audio", id: a.id });
                ed.seek(a.at);
              }}
            >
              <Icon name={a.role === "voice" ? "volume" : "music"} size={16} />
              <span className="flex-1 min-w-0">
                <strong className="block text-sm truncate">{a.title}</strong>
                <small className="block text-xs text-st-muted">
                  {fmt(a.at)} – {fmt(a.at + a.dur)}
                  {a.loop ? " · repeats" : ""}
                  {a.fadeIn > 0 || a.fadeOut > 0 ? " · fades" : ""}
                </small>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Settings of the selected music or voiceover piece. */
function AudioSettings({ ed, piece: a }: { ed: VideoEditor; piece: AudioItem }) {
  const set = (patch: Partial<AudioItem>) => ed.updateAudio(a.id, patch);
  const len = a.sourceDuration || 0;
  const room = Math.max(0.2, ed.total - a.at);
  const maxDur = a.loop || !len ? room : Math.min(room, Math.max(0.2, len - a.from));
  const maxFade = Math.max(0, Math.min(10, a.dur / 2));
  const inside = ed.t > a.at + 0.2 && ed.t < a.at + a.dur - 0.2;

  return (
    <div className="mt-3 pt-1 border-t border-st-line">
      <h3 className={PANEL_H3}>Selected: {a.title}</h3>

      <Slider
        label="Starts in the video at"
        value={r1(a.at)}
        min={0}
        max={r1(Math.max(0, ed.total - 0.2))}
        step={0.1}
        suffix=" s"
        onChange={(at) =>
          set({ at, dur: r1(Math.min(a.dur, Math.max(0.2, ed.total - at))) })
        }
      />

      {len > 0 && (
        <Slider
          label={`Starts this far into the ${a.role === "voice" ? "recording" : "song"}`}
          value={r1(a.from)}
          min={0}
          max={r1(Math.max(0, len - 0.2))}
          step={0.1}
          suffix=" s"
          onChange={(from) => {
            const most = a.loop ? room : Math.min(room, len - from);
            set({ from, dur: r1(Math.min(a.dur, Math.max(0.2, most))) });
          }}
        />
      )}

      <Slider
        label="Plays for"
        value={r1(a.dur)}
        min={0.2}
        max={r1(Math.max(0.2, maxDur))}
        step={0.1}
        suffix=" s"
        onChange={(dur) => set({ dur, toEnd: a.at + dur >= ed.total - 0.05 })}
      />

      <label className={CHECK}>
        <input
          type="checkbox"
          checked={a.loop}
          onChange={(e) => {
            const loop = e.target.checked;
            set({
              loop,
              dur: loop || !len ? a.dur : Math.min(a.dur, Math.max(0.2, len - a.from)),
            });
          }}
        />
        Repeat when it ends
      </label>

      <label className={CHECK}>
        <input
          type="checkbox"
          checked={a.toEnd}
          onChange={(e) => {
            const toEnd = e.target.checked;
            set({ toEnd, dur: toEnd ? maxDur : a.dur });
          }}
        />
        Play until the end of the video
      </label>

      <Slider
        label="Volume"
        value={Math.round(a.volume * 100)}
        min={0}
        max={200}
        suffix="%"
        onChange={(v) => set({ volume: v / 100 })}
      />

      <Slider
        label="Fade in"
        value={r1(Math.min(a.fadeIn, maxFade))}
        min={0}
        max={r1(maxFade)}
        step={0.1}
        suffix=" s"
        onChange={(fadeIn) => set({ fadeIn })}
      />

      <Slider
        label="Fade out"
        value={r1(Math.min(a.fadeOut, maxFade))}
        min={0}
        max={r1(maxFade)}
        step={0.1}
        suffix=" s"
        onChange={(fadeOut) => set({ fadeOut })}
      />

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className={cx(BTN, BTN_SMALL)}
          onClick={ed.split}
          disabled={!inside}
          title={inside ? "Split at the playhead (S)" : "Move the playhead inside this sound to split it"}
        >
          <Icon name="scissors" size={16} /> Split at {fmt(ed.t)}
        </button>
        <button
          type="button"
          className={cx(BTN, BTN_SMALL)}
          onClick={() => ed.seek(a.at)}
        >
          <Icon name="play" size={14} /> Go to start
        </button>
      </div>

      <button className={cx(LINK, LINK_DANGER)} onClick={() => ed.removeAudio(a.id)}>
        Remove this {a.role === "voice" ? "voiceover" : "track"}
      </button>
    </div>
  );
}

/* ================================================================
 * Voiceover
 * ================================================================ */

function VoicePanel({ ed }: PanelProps) {
  const { doc } = ed;
  const rec = ed.recording;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pieces = doc.audios.filter((a) => a.role === "voice");
  const selected =
    ed.sel?.type === "audio"
      ? pieces.find((a) => a.id === ed.sel?.id) ?? null
      : null;

  const [secs, setSecs] = useState<number>(0);

  useEffect(() => {
    if (!rec || rec.status === "done") {
      return undefined;
    }

    const id = window.setInterval(
      () => setSecs((Date.now() - rec.since) / 1000),
      200,
    );

    return () => window.clearInterval(id);
  }, [rec]);

  const supported =
    typeof window !== "undefined" &&
    !!window.MediaRecorder &&
    !!navigator.mediaDevices?.getUserMedia;

  function handleFilePick(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    void ed.uploadVoiceFiles(files);
  }

  const busy = ed.voiceUploading || ed.playing;

  return (
    <div className={PANEL_BODY}>
      <Title>Voiceover</Title>

      <p className={cx(HINT, HINT_TOP)}>
        Record yourself talking over the video, or upload audio files. New
        voiceovers start at the playhead ({fmt(ed.t)}); add as many as you need.
      </p>

      {!supported && (
        <p className="text-st-red text-[13px] mt-2">
          This browser cannot record audio. Try Chrome or Edge, or upload a file
          below.
        </p>
      )}

      {supported && (!rec || rec.status === "done") && (
        <button
          className="flex items-center justify-center gap-2.5 w-full min-h-[52px] rounded-2xl border border-st-line bg-white font-bold text-[15px] leading-none text-st-ink cursor-pointer hover:border-st-red disabled:opacity-50"
          onClick={ed.startRecording}
          disabled={busy}
        >
          <span className="w-4 h-4 rounded-full bg-st-red" />
          {pieces.length ? "Record another" : "Start recording"}
        </button>
      )}

      {rec && rec.status === "live" && (
        <button
          className="flex items-center justify-center gap-2.5 w-full min-h-[52px] rounded-2xl border border-st-red bg-[#fbe9e5] font-bold text-[15px] leading-none text-st-red cursor-pointer"
          onClick={ed.stopRecording}
        >
          <span className="w-3.5 h-3.5 rounded-[3px] bg-st-red animate-pulse" />
          Stop · {fmt(secs)}
        </button>
      )}

      {(!rec || rec.status === "done") && (
        <>
          <button
            type="button"
            className={cx(BTN, BTN_SMALL, BTN_BLOCK, "mt-2")}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            <Icon name="upload" size={16} />
            {ed.voiceUploading ? "Uploading…" : "Upload voiceover files"}
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus,.webm"
            multiple
            hidden
            onChange={handleFilePick}
          />
        </>
      )}

      <p className={FINEPRINT}>
        Tip: use earphones so the video's sound isn't picked up again.
      </p>

      {pieces.length > 0 && (
        <>
          <h3 className={PANEL_H3}>On the timeline</h3>
          <AudioPieces ed={ed} pieces={pieces} />
          <label className={CHECK}>
            <input
              type="checkbox"
              checked={doc.duck}
              onChange={(e) => ed.setDoc((d) => ({ ...d, duck: e.target.checked }))}
            />
            Lower the music while I speak
          </label>
        </>
      )}

      {selected && <AudioSettings ed={ed} piece={selected} />}

      <h3 className={PANEL_H3}>Or use an uploaded track</h3>

      <MusicLibrary
        tracks={ed.tracks}
        usedIds={pieces.map((a) => a.trackId)}
        onSelect={(track) => ed.addAudios([track], "voice")}
        onUploaded={(tracks) => {
          ed.setTracks((list) => [...tracks, ...(list || [])]);
          ed.addAudios(tracks, "voice");
        }}
        onDeleted={(trackId) => {
          ed.setTracks((list) => (list || []).filter((t) => t.id !== trackId));
          doc.audios
            .filter((a) => a.trackId === trackId)
            .forEach((a) => ed.removeAudio(a.id));
        }}
      />
    </div>
  );
}

/* ================================================================
 * Size
 * ================================================================ */

function SizePanel({ ed }: PanelProps) {
  const { doc } = ed;
  const first = doc.clips[0];

  const set = (patch: Partial<VideoDoc>) => {
    ed.setDoc(patch);
  };

  return (
    <div className={PANEL_BODY}>
      <Title>Size and quality</Title>

      <div className="grid grid-cols-2 gap-2">
        {ASPECTS.map((aspect) => {
          const on = doc.aspect === aspect.key;
          return (
            <button
              key={aspect.key}
              className={cx(
                "flex flex-col items-center gap-[3px] px-1.5 py-3 rounded-xl border bg-white cursor-pointer text-st-ink hover:border-[#9fb3ab]",
                on &&
                  "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_theme(colors.st.green)]",
                !on && "border-st-line",
              )}
              aria-pressed={on}
              onClick={() =>
                set({
                  aspect: aspect.key,
                })
              }
            >
              <span
                className="block border-2 border-current rounded my-1"
                style={aspectShape(
                  aspect.ratio ||
                    (first?.width ? first.width / (first.height || 1) : 9 / 16),
                )}
              />

              <strong className="text-sm">{aspect.label}</strong>

              <small className="text-xs text-st-muted">{aspect.hint}</small>
            </button>
          );
        })}
      </div>

      {doc.aspect !== "ORIGINAL" && (
        <>
          <h3 className={PANEL_H3}>Fit</h3>

          <div className={cx(SEG, SEG_WIDE_OR_FULL)}>
            <button
              aria-pressed={doc.fit === "FIT"}
              onClick={() =>
                set({
                  fit: "FIT",
                })
              }
            >
              Whole clip
            </button>

            <button
              aria-pressed={doc.fit === "FILL"}
              onClick={() =>
                set({
                  fit: "FILL",
                })
              }
            >
              Fill frame
            </button>
          </div>

          {doc.fit === "FIT" && (
            <>
              <h3 className={PANEL_H3}>Background</h3>

              <div className="flex flex-wrap gap-1.5 mb-1">
                <button
                  className={cx(
                    "h-8 px-3 rounded-full border border-st-line bg-gradient-to-br from-[#9fb3ab] to-[#dfe7e3] font-bold text-xs cursor-pointer",
                    doc.background === "BLUR" &&
                      "shadow-[0_0_0_2px_#fff,0_0_0_4px_theme(colors.st.green)]",
                  )}
                  onClick={() =>
                    set({
                      background: "BLUR",
                    })
                  }
                >
                  Blur
                </button>

                {[
                  "#000000",
                  "#ffffff",
                  "#0B7A5A",
                  "#10231E",
                  ...(ed.brand.colors || []).map((c) => c.toUpperCase()),
                ]
                  .filter((c, i, array) => array.indexOf(c) === i)
                  .map((color) => (
                    <button
                      key={color}
                      className={cx(
                        "w-8 h-8 rounded-full border border-black/[0.18] cursor-pointer p-0 hover:scale-110",
                        doc.background === color &&
                          "shadow-[0_0_0_2px_#fff,0_0_0_4px_theme(colors.st.green)]",
                      )}
                      style={{
                        background: color,
                      }}
                      onClick={() =>
                        set({
                          background: color,
                        })
                      }
                      aria-label={`Background ${color}`}
                    />
                  ))}

                <ColorPicker
                  value={doc.background === "BLUR" ? "#000000" : doc.background}
                  onChange={(value) =>
                    set({
                      background: (value || "#000000").toUpperCase(),
                    })
                  }
                  label="Any background colour"
                />
              </div>
            </>
          )}
        </>
      )}

      <h3 className={PANEL_H3}>Quality</h3>

      <div className="grid gap-2">
        <button
          className={cx(
            "text-left px-3.5 py-3 rounded-xl border bg-white cursor-pointer text-st-ink",
            doc.quality === "WHATSAPP"
              ? "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_theme(colors.st.green)]"
              : "border-st-line",
          )}
          onClick={() =>
            set({
              quality: "WHATSAPP",
            })
          }
        >
          <strong className="block text-sm">WhatsApp</strong>

          <small className="block text-xs text-st-muted mt-[3px] leading-[1.4]">
            720p, under 16 MB, up to{" "}
            {Math.round(ed.features.maxVideoSeconds / 60)} min. For chats and
            broadcasts.
          </small>
        </button>

        <button
          className={cx(
            "text-left px-3.5 py-3 rounded-xl border bg-white cursor-pointer text-st-ink",
            doc.quality === "HD"
              ? "border-st-green bg-st-green-soft shadow-[inset_0_0_0_1px_theme(colors.st.green)]"
              : "border-st-line",
          )}
          onClick={() =>
            set({
              quality: "HD",
            })
          }
        >
          <strong className="block text-sm">HD 1080p</strong>

          <small className="block text-xs text-st-muted mt-[3px] leading-[1.4]">
            Sharper, up to {Math.round(ed.features.maxHdVideoSeconds / 60)} min.
            For Status, Instagram, YouTube. May be too big to send in a chat.
          </small>
        </button>
      </div>
    </div>
  );
}

function aspectShape(ratio: number): CSSProperties {
  const max = 34;

  return ratio >= 1
    ? {
        width: max,
        height: max / ratio,
      }
    : {
        width: max * ratio,
        height: max,
      };
}

/* ================================================================
 * Music library
 * ================================================================ */

interface MusicLibraryProps {
  tracks: MusicTrack[] | null;
  /** Tracks already on the timeline (shown with a tick). */
  usedIds: Array<string | number>;
  onSelect: (track: MusicTrack) => void;
  /** Called once with every track that uploaded. */
  onUploaded: (tracks: MusicTrack[]) => void;
  onDeleted?: (trackId: string) => void;
}

function MusicLibrary({
  tracks,
  usedIds,
  onSelect,
  onUploaded,
  onDeleted,
}: MusicLibraryProps) {
  const [query, setQuery] = useState<string>("");

  const [mood, setMood] = useState<string>("");

  const [previewId, setPreviewId] = useState<string | number | null>(null);

  const [busy, setBusy] = useState<boolean>(false);
  const [progress, setProgress] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [copyrightModalOpen, setCopyrightModalOpen] = useState<boolean>(false);

  const audio = useRef<HTMLAudioElement | null>(null);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const moods = useMemo(
    () => [
      ...new Set(
        (tracks || [])
          .map((track) => track.mood)
          .filter((value): value is string => Boolean(value)),
      ),
    ],
    [tracks],
  );

  const shown = (tracks || []).filter(
    (track) =>
      (!mood || track.mood === mood) &&
      (!query ||
        `${track.title} ${track.artist || ""}`
          .toLowerCase()
          .includes(query.toLowerCase())),
  );

  function preview(track: MusicTrack) {
    const audioElement = audio.current;

    if (!audioElement) {
      return;
    }

    if (previewId === track.id) {
      audioElement.pause();
      setPreviewId(null);
      return;
    }

    audioElement.src = track.url;

    audioElement.currentTime = 0;

    audioElement
      .play()
      .then(() => setPreviewId(track.id))
      .catch(() => setPreviewId(null));
  }

  useEffect(() => {
    return () => {
      audio.current?.pause();
    };
  }, []);

  const MAX_MB = 15;

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || []);

    // Reset input so selecting the same files again works.
    if (fileRef.current) {
      fileRef.current.value = "";
    }

    if (!files.length) {
      return;
    }

    const tooBig = files.filter((f) => f.size > MAX_MB * 1024 * 1024);
    const ok = files.filter((f) => f.size <= MAX_MB * 1024 * 1024);
    setError(
      tooBig.length
        ? `Skipped (over ${MAX_MB} MB): ${tooBig.map((f) => f.name).join(", ")}`
        : null,
    );
    if (!ok.length) return;
    setPendingFiles(ok);
    setCopyrightModalOpen(true);
  }

  async function confirmMusicUpload() {
    if (!pendingFiles.length) {
      return;
    }

    const files = pendingFiles;

    setCopyrightModalOpen(false);
    setBusy(true);
    setError(null);

    const uploaded: MusicTrack[] = [];
    const failed: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : "Uploading…");
        try {
          // eslint-disable-next-line no-await-in-loop
          const track = await dispatch(
            uploadStudioMusic({
              file,
              title: file.name.replace(/\.[^.]+$/, ""),
              rightsConfirmed: true,
            }),
          ).unwrap();
          uploaded.push(track);
        } catch (err) {
          failed.push(
            `${file.name}: ${
              typeof err === "string"
                ? err
                : err instanceof Error
                  ? err.message
                  : "upload failed"
            }`,
          );
        }
      }
      if (uploaded.length) onUploaded(uploaded);
      if (failed.length) setError(`Couldn't upload ${failed.join(" · ")}`);
    } finally {
      setPendingFiles([]);
      setProgress(null);
      setBusy(false);
    }
  }

  function cancelMusicUpload() {
    setCopyrightModalOpen(false);
    setPendingFiles([]);
  }

  async function handleDeleteMusic(track: MusicTrack) {
    if (!track.mine) return;

    const confirmed = window.confirm(
      `Are you sure you want to remove "${track.title}"?`,
    );

    if (!confirmed) return;

    setBusy(true);
    setError(null);

    try {
      await dispatch(deleteStudioMusic(track.id)).unwrap();

      // Remove it from the current library immediately.
      onDeleted?.(track.id);
    } catch (err) {
      console.error("Failed to delete music:", err);

      setError(
        typeof err === "string"
          ? err
          : err instanceof Error
            ? err.message
            : "Couldn't delete that track.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <audio ref={audio} onEnded={() => setPreviewId(null)} />

      {copyrightModalOpen && pendingFiles.length > 0 && (
        <Modal
          title="Confirm music rights"
          onClose={cancelMusicUpload}
          width={520}
          footer={
            <>
              <button
                type="button"
                className={cx(BTN, BTN_SMALL)}
                onClick={cancelMusicUpload}
                disabled={busy}
              >
                Cancel
              </button>

              <button
                type="button"
                className={`min-h-[34px] px-[11px] py-[7px] text-[13px] rounded-[9px] text-st-line bg-st-green-dark`}
                onClick={confirmMusicUpload}
                disabled={busy}
              >
                {busy ? "Uploading…" : "I have the rights"}
              </button>
            </>
          }
        >
          <div className="px-1 py-2">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-st-green-soft text-st-green-dark">
                <Icon name="music" size={20} />
              </div>

              <div className="min-w-0">
                <h3 className="text-[15px] font-bold text-st-ink">
                  Do you have permission to use{" "}
                  {pendingFiles.length > 1 ? "these tracks" : "this music"}?
                </h3>

                <p className="mt-1 text-[13px] leading-[1.5] text-st-muted">
                  Before uploading this track, please confirm that you own the
                  music or have the necessary rights or permission to use it.
                </p>
              </div>
            </div>

            <ul className="mt-4 grid gap-1.5 max-h-[220px] overflow-y-auto rounded-xl border border-st-line bg-st-paper px-3.5 py-3">
              {pendingFiles.map((f) => (
                <li key={f.name + f.size} className="flex justify-between gap-3">
                  <span className="text-[13px] font-semibold text-st-ink break-words min-w-0">
                    {f.name}
                  </span>
                  <span className="flex-none text-xs text-st-muted">
                    {(f.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-3 text-xs leading-[1.5] text-st-muted">
              By continuing, you confirm that you have the legal right to use
              this music in your video.
            </p>
          </div>
        </Modal>
      )}

      <input
        className="w-full h-10 px-2.5 text-sm rounded-[10px] border border-st-line bg-st-paper text-st-muted mb-3 outline-none focus:border-st-green focus:bg-white"
        placeholder="Search music"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search music"
      />

      {moods.length > 0 && (
        <div className="flex flex-wrap gap-1.5 my-2.5">
          <button
            className={cx(
              "border rounded-full px-[13px] py-[5px] text-xs font-medium cursor-pointer",
              !mood
                ? "bg-st-ink border-st-ink text-white"
                : "bg-white border-st-line text-st-ink",
            )}
            onClick={() => setMood("")}
          >
            All
          </button>

          {moods.map((itemMood) => (
            <button
              key={itemMood}
              className={cx(
                "border rounded-full px-[13px] py-[5px] text-xs font-medium cursor-pointer",
                mood === itemMood
                  ? "bg-st-ink border-st-ink text-white"
                  : "bg-white border-st-line text-st-ink",
              )}
              onClick={() => setMood(itemMood)}
            >
              {itemMood}
            </button>
          ))}
        </div>
      )}

      {tracks === null && <p className={HINT}>Loading music…</p>}

      {tracks && shown.length === 0 && (
        <p className={HINT}>
          {tracks.length
            ? "No match."
            : "No music yet. Upload a song you have the rights to use."}
        </p>
      )}

      <ul className="list-none m-0 mt-2.5 p-0 grid gap-1">
        {shown.map((track) => (
          <li
            key={track.id}
            className={cx(
              "flex items-center gap-2 px-2 py-1.5 rounded-xl border",
              usedIds.includes(track.id)
                ? "border-st-green bg-st-green-soft"
                : "border-transparent hover:bg-st-paper",
            )}
          >
            {/* Play / Pause */}
            <button
              type="button"
              className="w-[34px] h-[34px] flex-none rounded-full border-0 bg-st-ink text-white grid place-items-center cursor-pointer [&>svg]:ml-0.5"
              onClick={() => preview(track)}
              aria-label={
                previewId === track.id
                  ? `Stop ${track.title}`
                  : `Listen to ${track.title}`
              }
            >
              <Icon
                name={previewId === track.id ? "pause" : "play"}
                size={14}
              />
            </button>

            {/* Track information */}
            <button
              type="button"
              className="flex-1 min-w-0 text-left border-0 bg-transparent py-0.5 cursor-pointer text-st-ink"
              onClick={() => {
                audio.current?.pause();
                setPreviewId(null);
                onSelect(track);
              }}
            >
              <strong className="block text-sm max-w-28 truncate">
                {track.title}
              </strong>

              <small className="block text-xs text-st-muted truncate">
                {[
                  track.artist,
                  track.mood,
                  track.durationSec
                    ? fmt(track.durationSec).replace(/\.\d$/, "")
                    : null,
                  track.mine ? "Your upload" : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </small>
            </button>

            {/* Selected check */}
            {usedIds.includes(track.id) && (
              <Icon
                name="check"
                size={18}
                className="text-st-green flex-none"
              />
            )}

            {/* Delete user's uploaded music */}
            {track.mine && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteMusic(track);
                }}
                disabled={busy}
                aria-label={`Delete ${track.title}`}
                title="Delete track"
                className={cx(
                  "flex-none w-8 h-8 rounded-lg border-0",
                  "grid place-items-center",
                  "text-st-muted hover:text-st-red hover:bg-red-50",
                  "cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <Icon name="trash" size={16} />
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-[18px] pt-3.5 border-t border-st-line">
        <button
          type="button"
          className={cx(BTN, BTN_SMALL, BTN_BLOCK)}
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          <Icon name="upload" size={16} />
          {busy ? progress || "Uploading…" : "Upload your own music"}
        </button>

        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus,.webm"
          multiple
          hidden
          onChange={handleFileChange}
        />

        <p className={FINEPRINT}>
          Uploads are kept in Gallery. Only upload music you own or have
          permission to use.
        </p>

        <p className={FINEPRINT}>
          MP3, M4A, AAC, WAV or OGG up to {MAX_MB} MB each. You can pick several
          files at once.
        </p>

        {error && (
          <p className="text-st-red text-[13px] mt-2" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
